import cx from 'classnames';
import { Fragment, useCallback } from 'react';

import { Checkbox, Icon, TableCell, TableHead, TableRow, TableSortLabel } from '@mui/material';
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";

// REQ-1.2: Selection counter component
import SelectionCounter from './SelectionCounter';
// REQ-1.5: Notification system for limit warnings

const TableHeader = (props) => {
    // REQ-1.1: Destructure new selection limit props
    const { 
        disableItemSelection = false, 
        columnsView, 
        onChangeColumns, 
        hideDragable, 
        selectedItems, 
        onChageSelectedItems, 
        dataItems, 
        sort, 
        lockedColumns, 
        menuOptions, 
        lockedColumnsHandler, 
        customColumn,
        // REQ-1.1: Selection limit props (default 500)
        maxSelectableItems = 500,
        showSelectionCounter = true,
        onSelectionLimitReached,
        messages,
        // Batch sequential: processed items to skip on Select All
        processedItemIds = new Set(),
        keyColumn = 'id',
    } = props;

    // REQ-1.3: Handler for Select All with limit enforcement
    // BUG-FIX: Changed to toggle based on current selection state instead of event.target.checked
    // This fixes the issue where clicking the checkbox when at bulk limit didn't uncheck items
    const handleSelectAll = () => {
        // If any items are selected, clear all (toggle off)
        if (selectedItems.length > 0) {
            // REQ-1.6: Deselection always allowed
            onChageSelectedItems([]);
            return;
        }
        
        // Batch sequential: filter out already-processed items before selecting
        const resolveKey = (item) => typeof keyColumn === 'function' ? keyColumn(item, 0) : item?.[keyColumn];
        const availableItems = processedItemIds.size > 0
            ? dataItems.filter(item => !processedItemIds.has(resolveKey(item)))
            : dataItems;

        // No items selected, so select up to limit (toggle on)
        // REQ-1.3: Check if available data exceeds limit
        if (availableItems.length > maxSelectableItems) {
            // Select only up to limit
            const limitedSelection = availableItems.slice(0, maxSelectableItems);
            onChageSelectedItems(limitedSelection);
            
            // Call optional callback
            const excess = availableItems.length - maxSelectableItems;
            onSelectionLimitReached?.(maxSelectableItems, availableItems.length, excess);
        } else if (availableItems.length > 0) {
            // Under limit - select all available
            onChageSelectedItems(availableItems);
        }
    };

    // REQ-1.3: Determine checkbox state - indeterminate when partial selection due to limit
    // Batch sequential: calculate available items (excluding processed)
    const resolveKeyForState = (item) => typeof keyColumn === 'function' ? keyColumn(item, 0) : item?.[keyColumn];
    const availableCount = processedItemIds.size > 0
        ? dataItems.filter(item => !processedItemIds.has(resolveKeyForState(item))).length
        : dataItems.length;
    const isPartialSelection = selectedItems.length > 0 && selectedItems.length < availableCount;
    const isAllSelected = selectedItems.length === availableCount && availableCount > 0;
    // Show indeterminate when at limit but not all available items selected
    const isAtLimitPartial = selectedItems.length === maxSelectableItems && availableCount > maxSelectableItems;
    const showIndeterminate = isPartialSelection || isAtLimitPartial;
    // BUG-FIX: Checkbox should be checked when items are selected (including at-limit state)
    // This ensures clicking it triggers the 'uncheck' branch of handleSelectAll
    const isChecked = isAllSelected || isAtLimitPartial;

    // REQ-10: Helper to get aria-sort value for a column
    const getAriaSort = (columnKey) => {
        if (sort?.key !== columnKey) return undefined;
        return sort?.order === 'asc' ? 'ascending' : 'descending';
    };

    // REQ-10: Keyboard handler for non-draggable headers (sorting only, no reordering)
    const handleNonDraggableKeyDown = (e, item) => {
        const { key } = e;
        // Enter/Space to toggle sort if sortable
        if ((key === 'Enter' || key === ' ') && item.sort) {
            e.preventDefault();
            let newSort = sort;
            if (sort?.key === item.key) {
                newSort = { key: item.key, order: sort?.order === 'asc' ? 'desc' : 'asc' };
            } else {
                newSort = { key: item.key, order: 'asc' };
            }
            onChangeSort(newSort);
        }
    };



    return (
        <TableHead>
            {
                hideDragable ?
                    <TableRow>
                        {
                            /* REQ-1.2: Checkbox cell with SelectionCounter */
                            !disableItemSelection &&
                            <TableCell padding="checkbox" className={cx(lockedColumns && "locked-header")}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    {/* REQ-1.3: Select All checkbox with limit enforcement */}
                                    <Checkbox
                                        style={{ color: "black" }}
                                        indeterminate={showIndeterminate}
                                        checked={isChecked}
                                        onClick={handleSelectAll}
                                        inputProps={{ 'aria-label': 'select all items' }}
                                        size="small"
                                    />
                                    {/* REQ-1.2: Selection counter shows selected/max */}
                                    {showSelectionCounter && selectedItems.length > 0 && (
                                        <SelectionCounter
                                            selected={selectedItems.length}
                                            max={maxSelectableItems}
                                            processedCount={processedItemIds.size}
                                            messages={messages}
                                        />
                                    )}
                                </div>
                            </TableCell>
                        }
                        {
                            menuOptions &&
                            <TableCell
                                padding="checkbox"
                                className={cx("context-menu-container", lockedColumns && "locked-header")}
                                ref={(el) => {
                                    if (el && lockedColumns) {
                                        const leftValue = disableItemSelection ? 0 : 43;
                                        el.style.setProperty('left', `${leftValue}px`, 'important');
                                    }
                                }}
                            >
                                <div style={{ width: 43 }}></div>
                            </TableCell>
                        }
                        {
                            customColumn &&
                            <TableCell
                                padding="checkbox"
                                className={cx("context-menu-container", lockedColumns && "locked-header")}
                                ref={(el) => {
                                    if (el && lockedColumns) {
                                        const leftValue = (disableItemSelection ? 0 : 43) + (menuOptions ? 43 : 0);
                                        el.style.setProperty('left', `${leftValue}px`, 'important');
                                    }
                                }}
                            >
                                <div style={{ width: 43 }}>
                                    {
                                        customColumn?.sort ?
                                            <TableSortLabel
                                                active={sort?.key === customColumn.sort}
                                                direction={sort?.key === customColumn.sort ? sort?.order : 'asc'}
                                                onClick={() => {
                                                    let newSort = sort
                                                    if (sort?.key === customColumn.sort)
                                                        newSort = { key: customColumn.sort, order: sort?.order == 'asc' ? 'desc' : 'asc' }
                                                    else
                                                        newSort = { key: customColumn.sort, order: 'asc' }

                                                    onChangeSort(newSort)
                                                }}
                                                // REQ-10: Remove from tab order - keyboard sorting handled by header cell
                                                tabIndex={-1}
                                            >

                                            </TableSortLabel>
                                            : <></>
                                    }
                                </div>
                            </TableCell>
                        }
                        {
                            columnsView.map((item, index) => {
                                return (
                                    <TableCell
                                        key={index}
                                        style={{
                                            width: item.width,
                                            minWidth: item.width,
                                            height: 44,
                                            left: item.locked && lockedColumnsHandler(index, columnsView, disableItemSelection, "header")
                                        }}
                                        id={`header-${item.key}`}
                                        aria-sort={item.sort ? getAriaSort(item.key) : undefined}
                                        role="columnheader"
                                        scope="col"
                                        // REQ-10: Keyboard navigation - header cell is the only focusable element
                                        tabIndex={0}
                                        onKeyDown={(e) => handleNonDraggableKeyDown(e, item)}
                                        aria-label={`${item.title}${item.sort ? ', sortable, press Enter or Space to sort' : ''}`}
                                    >
                                        {
                                            item.sort ?
                                                <TableSortLabel
                                                    active={sort?.key === item.key}
                                                    direction={sort?.key === item.key ? sort?.order : 'asc'}
                                                    onClick={() => {
                                                        let newSort = sort
                                                        if (sort?.key === item.key)
                                                            newSort = { key: item.key, order: sort?.order == 'asc' ? 'desc' : 'asc' }
                                                        else
                                                            newSort = { key: item.key, order: 'asc' }

                                                        onChangeSort(newSort)
                                                    }}
                                                    // REQ-10: Remove from tab order - keyboard sorting handled by header cell
                                                    tabIndex={-1}
                                                >
                                                    <span className="cell-title" title={item.title}>{item.title}</span>
                                                </TableSortLabel>
                                                : <span className="cell-title" title={item.title}>{item.title}</span>

                                        }
                                    </TableCell>
                                )
                            })
                        }
                    </TableRow>
                    :
                    <DragDropList
                        items={columnsView}
                        keyName="key"
                        className="header_drag-drop-list-container"
                        direction="horizontal"
                        itemRenderer={(col) => {
                            return (
                                <div key={col.key} className="cell-title" title={col.title}>
                                    {col.title}
                                </div>
                            );
                        }}
                        onDragEnd={(result) => {
                            if (!result.destination) return;

                            const newColumns = [...columnsView];

                            const [reorderedItem] = newColumns.splice(result.source.index, 1);
                            newColumns.splice(result.destination.index, 0, reorderedItem);

                            onChangeColumns(newColumns);
                        }}
                        {...props}
                    />
            }

        </TableHead>
    )
}

export default TableHeader;

const DragDropList = (props) => {
    // REQ-1.1: Destructure selection limit props for DragDropList variant
    const { 
        disableItemSelection = false, 
        items, 
        keyName, 
        itemRenderer, 
        onDragEnd, 
        direction, 
        isDragDisabled, 
        sort, 
        onChangeSort, 
        selectedItems, 
        onChageSelectedItems, 
        lockedColumns, 
        lockedColumnsHandler, 
        onChangeColumns, 
        menuOptions, 
        customColumn,
        // REQ-1.1: Selection limit props
        maxSelectableItems = 500,
        showSelectionCounter = true,
        onSelectionLimitReached,
        messages,
        dataItems,
        // Batch sequential
        processedItemIds = new Set(),
        keyColumn = 'id',
    } = props;

    // REQ-1.3: Handler for Select All with limit enforcement (DragDropList variant)
    // BUG-FIX: Changed to toggle based on current selection state instead of event.target.checked
    const handleSelectAllDragDrop = () => {
        // If any items are selected, clear all (toggle off)
        if (selectedItems.length > 0) {
            onChageSelectedItems([]);
            return;
        }
        
        // Batch sequential: filter out already-processed items
        const resolveKey = (item) => typeof keyColumn === 'function' ? keyColumn(item, 0) : item?.[keyColumn];
        const availableItems = processedItemIds.size > 0
            ? dataItems.filter(item => !processedItemIds.has(resolveKey(item)))
            : dataItems;

        // No items selected, so select up to limit (toggle on)
        if (availableItems.length > maxSelectableItems) {
            const limitedSelection = availableItems.slice(0, maxSelectableItems);
            onChageSelectedItems(limitedSelection);
            const excess = availableItems.length - maxSelectableItems;
            onSelectionLimitReached?.(maxSelectableItems, availableItems.length, excess);
        } else if (availableItems.length > 0) {
            onChageSelectedItems(availableItems);
        }
    };

    // REQ-1.3: Checkbox state for DragDropList variant
    const resolveKeyDD = (item) => typeof keyColumn === 'function' ? keyColumn(item, 0) : item?.[keyColumn];
    const availableCountDD = processedItemIds.size > 0
        ? dataItems.filter(item => !processedItemIds.has(resolveKeyDD(item))).length
        : dataItems.length;
    const isPartialSelectionDD = selectedItems.length > 0 && selectedItems.length < availableCountDD;
    const isAllSelectedDD = selectedItems.length === availableCountDD && availableCountDD > 0;
    const isAtLimitPartialDD = selectedItems.length === maxSelectableItems && availableCountDD > maxSelectableItems;
    const showIndeterminateDD = isPartialSelectionDD || isAtLimitPartialDD;
    // BUG-FIX: Checkbox should be checked when items are selected (including at-limit state)
    const isCheckedDD = isAllSelectedDD || isAtLimitPartialDD;

    // REQ-10: Helper to get aria-sort value for a column
    const getAriaSortDD = (columnKey) => {
        if (sort?.key !== columnKey) return undefined;
        return sort?.order === 'asc' ? 'ascending' : 'descending';
    };

    // REQ-10: Keyboard handler for column reordering
    const handleColumnKeyDown = useCallback((e, index, item) => {
        // Skip if column is locked (not draggable)
        if (lockedColumns && item.locked) return;

        const { key } = e;
        
        // Arrow keys for column reordering
        if (key === 'ArrowLeft' && index > 0) {
            e.preventDefault();
            const newColumns = [...items];
            // Find first non-locked column to the left
            let targetIndex = index - 1;
            while (targetIndex >= 0 && lockedColumns && newColumns[targetIndex].locked) {
                targetIndex--;
            }
            if (targetIndex >= 0) {
                const [movedItem] = newColumns.splice(index, 1);
                newColumns.splice(targetIndex, 0, movedItem);
                onChangeColumns(newColumns);
                // Announce to screen reader
                const announcement = messages?.ariaColumnMovedLeft?.replace('{column}', item.title)
                    || `${item.title} column moved left`;
                announceToScreenReader(announcement);
            }
        } else if (key === 'ArrowRight' && index < items.length - 1) {
            e.preventDefault();
            const newColumns = [...items];
            // Find first non-locked column to the right
            let targetIndex = index + 1;
            while (targetIndex < newColumns.length && lockedColumns && newColumns[targetIndex].locked) {
                targetIndex++;
            }
            if (targetIndex < newColumns.length) {
                const [movedItem] = newColumns.splice(index, 1);
                newColumns.splice(targetIndex, 0, movedItem);
                onChangeColumns(newColumns);
                // Announce to screen reader
                const announcement = messages?.ariaColumnMovedRight?.replace('{column}', item.title)
                    || `${item.title} column moved right`;
                announceToScreenReader(announcement);
            }
        } else if (key === 'Enter' || key === ' ') {
            // Enter/Space to toggle sort if sortable
            if (item.sort) {
                e.preventDefault();
                let newSort = sort;
                if (sort?.key === item.key) {
                    newSort = { key: item.key, order: sort?.order === 'asc' ? 'desc' : 'asc' };
                } else {
                    newSort = { key: item.key, order: 'asc' };
                }
                onChangeSort(newSort);
            }
        }
    }, [items, lockedColumns, onChangeColumns, sort, onChangeSort, messages]);

    // REQ-10: Helper to announce changes to screen readers
    const announceToScreenReader = (message) => {
        const announcement = document.createElement('div');
        announcement.setAttribute('role', 'status');
        announcement.setAttribute('aria-live', 'polite');
        announcement.setAttribute('aria-atomic', 'true');
        announcement.style.cssText = 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0';
        announcement.textContent = message;
        document.body.appendChild(announcement);
        setTimeout(() => document.body.removeChild(announcement), 1000);
    };


    return (
        <Fragment>
            <DragDropContext
                onDragStart={() => {
                    props.onColumnDragStart?.();
                }}
                onDragEnd={(result) => {
                    onDragEnd(result);
                    props.onColumnDragEnd?.();
                }}
                alwaysRenderSuggestions={true}
            >
                <Droppable
                    droppableId="droppable"
                    direction={direction || 'vertical'}
                    // Phase 7: ignoreContainerClipping allows drag-drop to work within scroll containers
                    // without needing overflow:visible manipulation (which caused row re-renders)
                    ignoreContainerClipping={true}
                    // Phase 6 (Feature 3): Portal-based renderClone fixes headers disappearing when dragging at non-zero scroll
                    // The clone is rendered in document.body instead of the scroll container
                    getContainerForClone={() => document.body}
                    renderClone={(provided, snapshot, rubric) => {
                        const item = items[rubric.source.index];
                        return (
                            <div
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                ref={provided.innerRef}
                                className="droppable-cell droppable-clone"
                                style={{
                                    ...provided.draggableProps.style,
                                    // Ensure clone appears above sticky header
                                    zIndex: 9999,
                                    // Match header cell appearance
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: '#fff',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '4px',
                                    padding: '8px 16px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    // Lock width to original cell width
                                    width: item.width || 'auto',
                                    minWidth: item.width || 100,
                                    height: 44,
                                }}
                            >
                                {itemRenderer ? itemRenderer(item) : item[keyName]}
                            </div>
                        );
                    }}
                >
                    {(provided, snapshot) => (
                        <TableRow
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={cx("drag-drop-list-container", snapshot.isDraggingOver ? 'dragged' : 'dragging')}
                        >
                            {
                                /* REQ-1.2: Checkbox cell with SelectionCounter (DragDropList variant) */
                                !disableItemSelection &&
                                <TableCell padding="checkbox" className={cx(lockedColumns && "locked-header")}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <Checkbox
                                            style={{ color: "black" }}
                                            indeterminate={showIndeterminateDD}
                                            checked={isCheckedDD}
                                            onClick={handleSelectAllDragDrop}
                                            inputProps={{ 'aria-label': 'select all items' }}
                                            size="small"
                                        />
                                        {showSelectionCounter && selectedItems.length > 0 && (
                                            <SelectionCounter
                                                selected={selectedItems.length}
                                                max={maxSelectableItems}
                                                messages={messages}
                                            />
                                        )}
                                    </div>
                                </TableCell>
                            }
                            {
                                menuOptions &&
                                <TableCell
                                    padding="checkbox"
                                    className={cx("context-menu-container", lockedColumns && "locked-header")}
                                    ref={(el) => {
                                        if (el && lockedColumns) {
                                            const leftValue = disableItemSelection ? 0 : 43;
                                            el.style.setProperty('left', `${leftValue}px`, 'important');
                                        }
                                    }}
                                >
                                    <div style={{ width: 43 }}></div>
                                </TableCell>
                            }
                            {
                                customColumn &&
                                <TableCell
                                    padding="checkbox"
                                    className={cx("context-menu-container", lockedColumns && "locked-header")}
                                    ref={(el) => {
                                        if (el && lockedColumns) {
                                            const leftValue = (disableItemSelection ? 0 : 43) + (menuOptions ? 43 : 0);
                                            el.style.setProperty('left', `${leftValue}px`, 'important');
                                        }
                                    }}
                                >
                                    <div style={{ width: 43, display: 'flex', justifyContent: 'center' }}>
                                        {
                                            customColumn?.sort ?
                                                <TableSortLabel
                                                    active={sort?.key === customColumn.sort}
                                                    direction={sort?.key === customColumn.sort ? sort?.order : 'asc'}
                                                    onClick={() => {
                                                        let newSort = sort
                                                        if (sort?.key === customColumn.sort)
                                                            newSort = { key: customColumn.sort, order: sort?.order == 'asc' ? 'desc' : 'asc' }
                                                        else
                                                            newSort = { key: customColumn.sort, order: 'asc' }

                                                        onChangeSort(newSort)
                                                    }}
                                                    // REQ-10: Remove from tab order - keyboard sorting handled by header cell
                                                    tabIndex={-1}
                                                >
                                                </TableSortLabel>
                                                : <></>
                                        }
                                    </div>
                                </TableCell>
                            }
                            {
                                items && items.map((item, index) => (
                                    <Draggable
                                        key={`${item[keyName]}`}
                                        draggableId={`${item[keyName]}`}
                                        index={index}
                                        isDragDisabled={isDragDisabled || (lockedColumns && item.locked)}
                                    >

                                        {(provided, snapshot) => {
                                            // REQ-8.1: Determine if column is fixed (no drag allowed)
                                            const isFixedColumn = lockedColumns && item.locked;
                                            
                                            return (
                                                // CD-01 + CD-02: provided props on <TableCell> so rbd's transform
                                                // moves the entire cell (borders + content) as a single unit
                                                // REQ-8.1: dragHandleProps moved to dedicated grip icon to separate from resizer
                                                <TableCell
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    className={cx(lockedColumns && item.locked && "locked-header")}
                                                    style={{
                                                        ...provided.draggableProps.style,
                                                        width: item.width,
                                                        minWidth: item.width,
                                                        height: 44,
                                                        left: item.locked && lockedColumnsHandler(index, items, disableItemSelection, "header"),
                                                    }}
                                                    id={`header-${item.key}`}
                                                    // REQ-10: Accessibility enhancements
                                                    aria-sort={item.sort ? getAriaSortDD(item.key) : undefined}
                                                    role="columnheader"
                                                    scope="col"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => handleColumnKeyDown(e, index, item)}
                                                    aria-label={`${item.title}${item.sort ? ', sortable' : ''}${!isFixedColumn ? ', press arrow keys to reorder' : ''}`}
                                                >
                                                    {/* REQ-8.1: ResizeCell wrapper with drag handle inside, positioned left of content */}
                                                    <ResizeCell columns={items} item={item} onChangeColumns={onChangeColumns} key={item?.key}>
                                                        {/* REQ-8.1: Drag handle - only shown for non-fixed columns, positioned left of label */}
                                                        {/* tabIndex={-1} prevents keyboard focus - users navigate columns via header cell */}
                                                        {!isFixedColumn && (
                                                            <div
                                                                {...provided.dragHandleProps}
                                                                className="column-drag-handle"
                                                                title={messages?.ariaDragToReorder || "Drag to reorder column"}
                                                                aria-label={messages?.ariaDragToReorder || "Drag to reorder column"}
                                                                aria-hidden="true"
                                                                tabIndex={-1}
                                                            >
                                                                <Icon className="drag-handle-icon">drag_indicator</Icon>
                                                            </div>
                                                        )}
                                                        {
                                                            item.sort ?
                                                                <TableSortLabel
                                                                    active={sort?.key === item.key}
                                                                    direction={sort?.key === item.key ? sort?.order : 'asc'}
                                                                    onClick={() => {
                                                                        let newSort = sort
                                                                        if (sort?.key === item.key)
                                                                            newSort = { key: item.key, order: sort?.order == 'asc' ? 'desc' : 'asc' }
                                                                        else
                                                                            newSort = { key: item.key, order: 'asc' }

                                                                        onChangeSort(newSort)
                                                                    }}
                                                                    // REQ-10: Remove from tab order - keyboard sorting handled by header cell
                                                                    tabIndex={-1}
                                                                >
                                                                    {itemRenderer && itemRenderer(item) || item[keyName]}
                                                                </TableSortLabel>
                                                                : itemRenderer && itemRenderer(item) || item[keyName]
                                                        }
                                                    </ResizeCell>
                                                </TableCell>
                                            );
                                        }}
                                    </Draggable>
                                ))
                            }
                            {provided.placeholder}
                        </TableRow>
                    )}
                </Droppable>
            </DragDropContext>
        </Fragment >
    );
}

// CD-01 + CD-02: ResizeCell no longer receives `provided` — rbd props are on <TableCell> now
const ResizeCell = (props) => {
    const { item, columns, onChangeColumns } = props

    // NI-04 Fix 1: Listeners only attached on mouseDown, properly cleaned on mouseUp
    // Eliminates O(N × M) listener leak from original code
    const handleMouseDown = (e) => {
        // Prevent any bubbling (defensive, though REQ-8.1 Option B makes this less critical)
        e.stopPropagation();
        e.preventDefault();
        
        const th = document.getElementById(`header-${item.key}`)
        if (!th) return;
        const startOffset = th.offsetWidth - e.pageX;

        const mouseMove = (ev) => {
            th.style.minWidth = startOffset + ev.pageX + 'px';
        };

        const mouseUp = () => {
            document.removeEventListener('mousemove', mouseMove);
            document.removeEventListener('mouseup', mouseUp);
            const size = parseInt(th.style.minWidth, 10);
            const index = columns.findIndex(x => x == item);
            const newColumns = [...columns];
            newColumns[index].width = size;
            onChangeColumns && onChangeColumns(newColumns);
        };

        document.addEventListener('mousemove', mouseMove);
        document.addEventListener('mouseup', mouseUp);
    };

    return (
        <Fragment>
            <div className="cell-container">
                {props.children}
            </div>
            {item?.resize &&
                <div
                    id={`resize-${item.key}`}
                    className="resizer"
                    onMouseDown={handleMouseDown}
                >
                </div>
            }
        </Fragment>
    )
}